import React, { Suspense, useEffect } from "react";
import "cloud-matrix-ui/cloudMatrix.css";
import { useNavigate, useParams } from "react-router-dom";
import { useSettledFeatureFlag } from "shared-ui/src/hooks/useSettledFeatureFlag";
import LoadingOrError from "shared-ui/src/global-layout/LoadingOrError";
import { Box } from "@mui/material";
import { useClaims } from "../../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../../auth/authorizationTypes";

const CloudMatrix = React.lazy(() =>
  import("cloud-matrix-ui").then((m) => ({ default: m.CloudMatrix })),
);

const CloudMatrixPage: React.FC = () => {
  const navigate = useNavigate();
  const { prefix } = useParams<{ prefix?: string }>();
  const { enabled: cloudMatrixEnabled, isLoading } = useSettledFeatureFlag(
    "spu.enable-cloud-matrix-assessments",
  );
  const { hasPermission } = useClaims();
  const hasAccess = cloudMatrixEnabled && hasPermission(ShiOnePermission.cloudMatrixRead);

  useEffect(() => {
    if (!isLoading && !hasAccess) {
      navigate(prefix ? `/${ prefix }/assessments` : "/assessments", { replace: true });
    }
  }, [isLoading, hasAccess, navigate, prefix]);

  if (isLoading) return <LoadingOrError loading />;
  if (!hasAccess) return null;

  const basename = prefix
    ? `/${ prefix }/assessments/cloud-matrix`
    : "/assessments/cloud-matrix";

  const authConfig = {
    redirectUri: `${ window.location.origin }/auth/callback`,
    tenantId: import.meta.env.ADAL_CONFIG_TENANT
  };

  return (
    <Box sx={{ paddingTop: 2 }}>
      <Suspense fallback={<LoadingOrError loading />}>
        <CloudMatrix basename={basename} authConfig={authConfig} />
      </Suspense>
    </Box>
  );
};

export default CloudMatrixPage;
